import {
  createAsyncThunk,
  type ActionReducerMapBuilder,
  type AsyncThunk,
  type Draft,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";

type ThunkApi = { rejectValue: string };

export const createApiThunk = <Returned, Arg>(
  typePrefix: string,
  apiCall: (arg: Arg) => Promise<Returned>,
  fallbackError: string,
) =>
  createAsyncThunk<Returned, Arg, ThunkApi>(
    typePrefix,
    async (arg, { rejectWithValue }) => {
      try {
        return await apiCall(arg);
      } catch (error) {
        if (error instanceof AxiosError) {
          return rejectWithValue(
            error.response?.data?.message ?? fallbackError,
          );
        }
        return rejectWithValue(fallbackError);
      }
    },
  );

interface AddCasesOptions<State, Returned, Arg> {
  builder: ActionReducerMapBuilder<State>;
  thunk: AsyncThunk<Returned, Arg, ThunkApi>;
  loadingKey: string;
  onSuccess?: (state: Draft<State>, payload: Returned) => void;
  onError?: (state: Draft<State>) => void;
  fallbackError: string;
}

export const addAsyncCases = <State, Returned, Arg>({
  builder,
  thunk,
  loadingKey,
  onSuccess,
  onError,
  fallbackError,
}: AddCasesOptions<State, Returned, Arg>) => {
  builder
    .addCase(thunk.pending, (state) => {
      const s = state as Draft<State> & {
        loading: Record<string, boolean>;
        error: string | null;
      };
      s.loading[loadingKey] = true;
      s.error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      const s = state as Draft<State> & {
        loading: Record<string, boolean>;
        error: string | null;
      };
      s.loading[loadingKey] = false;
      s.error = null;
      if (onSuccess) onSuccess(state as Draft<State>, action.payload);
    })
    .addCase(thunk.rejected, (state, action) => {
      const s = state as Draft<State> & {
        loading: Record<string, boolean>;
        error: string | null;
      };
      s.loading[loadingKey] = false;
      s.error = action.payload ?? action.error.message ?? fallbackError;
      if (onError) onError(state as Draft<State>);
    });
};
